import React, { ReactElement, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import LoadingIndicator from '@Common/LoadingIndicator/LoadingIndicator';
import LocationTaskSidebar from '../../../../TaskList/TaskDetail/LocationTaskSidebar/LocationTaskSidebar';
import {
    useGetBreakdownTasks,
    BreakdownTask,
    BreakdownTasksParams,
    OverviewLocationFilters,
    BREAKDOWN_TASKS_QUERY_KEY,
    LOCATION_BREAKDOWN_QUERY_KEY,
    OVERVIEW_TAB_QUERY_KEY,
} from '@Apis/companyDashboard';
import { useSearchResponsibleUsers } from '@Apis/todaysFocus';
import { formatCurrentDate } from '@Helpers/dateTime';
import { CORE_STATUSES } from '@Constants/constants';
import { Option } from '@Types/common';
import { BreakdownSelection, BREAKDOWN_METRIC_LABELS } from '../breakdownSelection';
import './LocationDetailTasksTile.scss';

interface LocationDetailTasksTileProps {
    /**
     * The breakdown value the user drilled into. Null whenever the sidebar was opened from a
     * plain location row, which is the one state with nothing to list — the tab is reachable
     * from the tab strip like any other, and lands on its empty state rather than on a request
     * with no group to scope it.
     */
    selection?: BreakdownSelection | null;
    /** Which configurable Overview tab the panel sits under, so project scoping matches it. */
    tabId: number;
    /** The page's active filters, so this list holds the tasks the clicked number counted. */
    filters: OverviewLocationFilters;
}

/**
 * Reads the assignee off a task. The endpoint sends the responsible user's name when there is
 * one and the job title the task falls to when there is not, so "unassigned" here means both
 * are empty — the same thing the Unassigned Work count means.
 */
const assigneeOf = (task: BreakdownTask): string => {
    const name = `${task.responsibleFirstName} ${task.responsibleLastName}`.trim();
    if (name) return name;
    return task.jobTitleName || 'Unassigned';
};

/**
 * How far off the due date is, in the user's own terms. `daysUntilDue` is signed and computed
 * server-side against the user's today, so this never has to work out "today" in the browser
 * and can never disagree with the metric the list was reached from.
 */
const duePhrase = (days: number | null): string => {
    if (days === null) return '';
    if (days === 0) return 'due today';
    if (days > 0) return `${days}d away`;
    return `${Math.abs(days)}d late`;
};

type StatusTone = 'progress' | 'complete' | 'notStarted' | 'default';

// Keyed by core status, not by `statusName`'s free per-customer text, so two subscriptions'
// differently-named statuses that share a core status still read the same color — the same
// rule `StatusSelector`'s `getStatusColors` colors every other status pill in the app by.
const statusTone = (coreStatusId: number | null | undefined): StatusTone => {
    switch (coreStatusId) {
        case CORE_STATUSES.NotStarted: return 'notStarted';
        case CORE_STATUSES.InProgress: return 'progress';
        case CORE_STATUSES.Complete: return 'complete';
        default: return 'default';
    }
};

const LocationDetailTasksTile = ({ selection = null, tabId, filters }: LocationDetailTasksTileProps): ReactElement => {
    // 0 means no task is open. Kept here rather than lifted: the sidebar needs nothing this
    // tile doesn't already have (the location key, and a way to refresh what it changed).
    const [selectedTaskId, setSelectedTaskId] = useState<number>(0);

    const queryClient = useQueryClient();

    // Null until a value has been clicked, which is what keeps the query from firing for a
    // sidebar opened off a plain location row.
    const params: BreakdownTasksParams | null = useMemo(
        () =>
            selection
                ? {
                    locationKey: selection.locationKey,
                    groupBy: selection.view,
                    groupId: selection.groupId,
                    metric: selection.metric,
                    tabId,
                    filters,
                }
                : null,
        [selection, tabId, filters]
    );

    const { data, isLoading, error } = useGetBreakdownTasks(params);

    /**
     * A save in the task sidebar can change a task's status, dates or assignee — any of which
     * can move it out of the metric it was listed under, and change the number that metric was
     * clicked with. Refetching this list alone would leave the breakdown panel and the location
     * tile behind the sidebar showing counts the list no longer agrees with, so all three are
     * invalidated together.
     */
    const refreshAfterSave = () => {
        queryClient.invalidateQueries({ queryKey: [BREAKDOWN_TASKS_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [LOCATION_BREAKDOWN_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [OVERVIEW_TAB_QUERY_KEY] });
    };

    // The sidebar can reassign the task, which the "Assign" control needs candidates for —
    // scoped to this one location the same way LocationDetailContactsTile scopes its own
    // candidate search, rather than every responsible user in the subscription.
    const { data: responsibleUsersData } = useSearchResponsibleUsers({
        locationKeys: selection ? [selection.locationKey] : [],
        searchTerm: '',
    });
    const responsibleUsers: Option[] = useMemo(
        () => responsibleUsersData.users.map((u) => ({ id: u.id, name: u.name, key: u.id, value: u.id.toString() })),
        [responsibleUsersData.users]
    );

    const closeTaskDrawer = () => setSelectedTaskId(0);
    const openTaskDrawer = (taskId: number) => setSelectedTaskId(taskId);
    const handleTaskRowKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, taskId: number) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        openTaskDrawer(taskId);
    };

    if (!selection) {
        return (
            <div className='location-detail-tasks-tile'>
                <div className='location-detail-tasks-tile-empty'>No tasks selected</div>
            </div>
        );
    }

    const tasks = data?.results ?? [];
    // `data !== undefined`, not `!isLoading`: a query disabled before it ever resolved reports
    // `isLoading: false` with no data, and gating the empty state on that would tell the user
    // there are no tasks without ever having asked.
    const hasLoaded = data !== undefined;
    const total = data?.totalCount ?? 0;
    // The page size is the ceiling on one request, so a very large group can come back short of
    // its own total. Said plainly rather than letting the list quietly disagree with the number
    // that was clicked.
    const isTruncated = total > tasks.length;

    return (
        <div className='location-detail-tasks-tile'>

            {/* The count is the total the metric was clicked with, not how many of the list
                loaded — the footer says that. Trailing rather than leading so it reads as a
                count of tasks whatever the metric is named ("Complete for X · 5 tasks"),
                instead of forcing every label to double as a plural noun. */}
            <div className='location-detail-tasks-tile-summary'>
                {BREAKDOWN_METRIC_LABELS[selection.metric]} for{' '}
                <strong className='location-detail-tasks-tile-group'>{selection.groupName}</strong>
                {hasLoaded && !error && ` · ${total} task${total === 1 ? '' : 's'}`}
            </div>

            {error && (
                <div className='location-detail-tasks-tile-empty'>
                    Unable to load tasks for {selection.groupName}.
                </div>
            )}

            {!error && (
                <LoadingIndicator isLoading={isLoading} hideWhileLoading>
                    {tasks.length > 0 && (
                        <ul className='location-detail-tasks-list'>
                            {tasks.map((task) => {
                                const isPastDue = (task.daysUntilDue ?? 0) < 0;
                                const tone = statusTone(task.coreStatusId);
                                // Mirrors StatusSelector's `isPastDueNotStarted`: a Not Started task that is
                                // also overdue takes the red treatment instead of its usual gray one.
                                const isPastDueNotStarted = isPastDue && tone === 'notStarted';

                                return (
                                    <li key={task.id} className='location-detail-task'>
                                        {/* The control is inside the <li> rather than being it: a
                                            role='button' on the list item itself would strip the list
                                            of its rows, so a screen reader could no longer say how many
                                            tasks the drill-down found. */}
                                        <div
                                            className='location-detail-task-hit'
                                            role='button'
                                            tabIndex={0}
                                            aria-label={`Open task: ${task.taskName}`}
                                            onClick={() => openTaskDrawer(task.id)}
                                            onKeyDown={(event) => handleTaskRowKeyDown(event, task.id)}>
                                            <div className='location-detail-task-name' title={task.taskName}>
                                                {task.taskName}
                                            </div>
                                            <div className='location-detail-task-row'>
                                                <span className={`location-detail-task-date${isPastDue ? ' location-detail-task-date--late' : ''}`}>
                                                    <i className='fa-regular fa-calendar' aria-hidden='true' />
                                                    <span>
                                                        {formatCurrentDate(task.targetDate)}
                                                        {duePhrase(task.daysUntilDue) && (
                                                            <>
                                                                {' · '}
                                                                {duePhrase(task.daysUntilDue)}
                                                            </>
                                                        )}
                                                    </span>
                                                    {/* Same warning glyph LocationTaskSidebar draws next to an overdue target date. */}
                                                    {isPastDue && (
                                                        <svg
                                                            width='12' height='12' viewBox='0 0 14 14' fill='none' aria-hidden='true'
                                                            className='location-detail-task-due-icon'>
                                                            <path
                                                                d='M7 1L13 13H1L7 1Z' fill='currentColor' fillOpacity='0.1'
                                                                stroke='currentColor' strokeWidth='1.3' strokeLinecap='round' strokeLinejoin='round' />
                                                            <path d='M7 5V8M7 10V10.5' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
                                                        </svg>
                                                    )}
                                                </span>
                                                {task.statusName && (
                                                    <span
                                                        className={`location-detail-task-status location-detail-task-status--${tone}${isPastDueNotStarted ? ' location-detail-task-status--past-due-override' : ''}`}>
                                                        <span className='location-detail-task-status-dot' />
                                                        {task.statusName}
                                                    </span>
                                                )}
                                            </div>
                                            <div className='location-detail-task-row'>
                                                <span className='location-detail-task-assignee'>
                                                    <i className='fa-regular fa-user' aria-hidden='true' />
                                                    {assigneeOf(task)}
                                                </span>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {hasLoaded && tasks.length === 0 && (
                        <div className='location-detail-tasks-tile-empty'>No tasks found</div>
                    )}
                    {hasLoaded && tasks.length > 0 && (
                        <div className='location-detail-tasks-tile-footer'>
                            {isTruncated
                                ? `Showing ${tasks.length} of ${total}`
                                : `${tasks.length} task${tasks.length === 1 ? '' : 's'} shown`}
                        </div>
                    )}
                </LoadingIndicator>
            )}

            {selectedTaskId > 0 && (
                <LocationTaskSidebar
                    isDrawerVisible={selectedTaskId > 0}
                    closeDrawer={closeTaskDrawer}
                    changeTask={openTaskDrawer}
                    onComplete={() => {
                        refreshAfterSave();
                        closeTaskDrawer();
                    }}
                    locationTaskId={selectedTaskId}
                    locationKey={selection.locationKey}
                    responsibleUsers={responsibleUsers}
                    isMobile={window.innerWidth <= 768}
                />
            )}

        </div>
    );
};

export default LocationDetailTasksTile;
